import classnames from 'classnames';
import { isEqual, isNull } from 'lodash/fp';
import React, { useMemo, useRef } from 'react';
import Select, { Options } from 'react-select';

import { useAppDispatch, useAppSelector } from '../../../hooks/react-redux';
import { getOriginalDocument } from '../../documents/my-documents/store';
import { customStyles } from '../../shared/dropdown/Dropdown';
import { CustomTooltip } from '../../shared/tooltip';
import { updateFieldValue, userCorrectAIFieldValue } from '../instances/store';
import { DropdownOption, DropdownOptions } from './Dropdown';
import styles from './Fields.module.scss';
import { fieldBoxShadow } from '../../constants/style';

const { green, amethyst, primary, gold, disabledGrey, amber } = styles;

interface PartySelectProps {
    id: string;
    value: string[] | null;
    index: number;
    sectionId?: string;
    groupIndex?: number;
    rowId?: string;
    disabled?: boolean;
    showFieldUpdated?: boolean;
    showDatasetUpdated?: boolean;
    showAIModified?: boolean;
    showAIModifiedUserCorrected?: boolean;
    isMLCorrection?: boolean;
    showClause?: boolean;
    isTable?: boolean;
    includedInAnnex?: boolean;
    menuPortalTarget?: HTMLElement | null;
    datasetId: number;
    parentFieldId: string;
    modalInstance?: boolean;
}

export const PartySelect: React.FC<PartySelectProps> = ({
    id,
    value,
    disabled = false,
    index,
    groupIndex,
    sectionId,
    rowId,
    showFieldUpdated = false,
    showDatasetUpdated = false,
    showAIModified = false,
    showAIModifiedUserCorrected = false,
    isMLCorrection = false,
    showClause = false,
    isTable = false,
    includedInAnnex = false,
    menuPortalTarget,
    datasetId,
    parentFieldId,
    modalInstance
}) => {
    const dropdownRef = useRef<HTMLDivElement>(null);
    const dispatch = useAppDispatch();
    const originalDocument = useAppSelector(getOriginalDocument);

    const updateValue = (value: null | string[]) => {
        if (isMLCorrection) {
            dispatch(userCorrectAIFieldValue(datasetId, parentFieldId, value, index, sectionId, groupIndex, rowId));
        } else {
            dispatch(updateFieldValue(datasetId, parentFieldId, value, index, sectionId, groupIndex, rowId, modalInstance));
        }
    };

    const updateDropdownValue = (option: DropdownOption | Options<DropdownOption> | null) => {
        let dropdownValue = null;
        if (!isNull(option)) {
            dropdownValue = [(option as DropdownOption).value];
        }
        if (!isEqual(dropdownValue, value)) {
            updateValue(dropdownValue);
        }
    };

    const mappedOptions: DropdownOptions = useMemo(() => {
        const partyA = originalDocument?.entityAName || 'Party A';
        const partyB = originalDocument?.entityBName || 'Party B';
        return [{ value: 'partyA', label: partyA }, { value: 'partyB', label: partyB }];
    }, [originalDocument]);
    const mappedValue: DropdownOptions | null = useMemo(() => value && mappedOptions.filter(option => value.includes(option.value)) || null, [value, mappedOptions]);

    const dropdownTooltip = useMemo(() => {
        if (!value || !disabled) {
            return null;
        }
        return value.join(' - ');
    }, [value, disabled]);

    const MAX_MENU_HEIGHT = 150;

    const placeholder = includedInAnnex ? 'See Agency Annex' : 'Select...';
    const isDisabled = useMemo(() => disabled || includedInAnnex, [disabled, includedInAnnex]);
    const controlBorderColor = useMemo(() => {
        if (!showDatasetUpdated && !showFieldUpdated && !showAIModified && !showAIModifiedUserCorrected) {
            return isDisabled ? disabledGrey : primary;
        }
        if (showAIModifiedUserCorrected) {
            return amber;
        }
        if (showAIModified) {
            return gold;
        }
        return showFieldUpdated ? amethyst : green;
    }, [showDatasetUpdated, showFieldUpdated, showAIModified, isDisabled, showAIModifiedUserCorrected]);

    const controlBoxShadow = useMemo(() => [amethyst, green, amber, gold].includes(controlBorderColor) ? fieldBoxShadow(controlBorderColor) : undefined, [controlBorderColor]);

    return (
        <CustomTooltip overlayText={dropdownTooltip} trigger='click'>
            <div
                data-testid={`definition-party-select-${id}`}
                className={classnames(styles.dropdownFieldWrapper, { [styles.withClauseLabel]: showClause && isTable })}
                ref={dropdownRef}
            >
                <Select
                    className={styles.dropdownField}
                    classNamePrefix='ark-dropdown'
                    isDisabled={isDisabled}
                    value={mappedValue}
                    onChange={updateDropdownValue}
                    isMulti={false}
                    options={mappedOptions}
                    styles={customStyles}
                    controlBorderColor={controlBorderColor}
                    controlBoxShadow={controlBoxShadow}
                    maxMenuHeight={MAX_MENU_HEIGHT}
                    menuPlacement='auto'
                    placeholder={placeholder}
                    menuPortalTarget={menuPortalTarget}
                    isClearable={!isDisabled}
                    fontWeight={500}
                />
            </div>
        </CustomTooltip>
    );
};
